/**
 * Deals Page - Main orchestrator
 * Browse IKEA deals with gamified mystery box system
 */

import { useState, useCallback, useEffect } from 'react';
import { TestTube2, Eye } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuthStore } from '../../../stores/auth-store';

// Hooks
import {
  useDealsCountries,
  useDetectCountry,
  useDealsSummary,
  useDealsProducts,
  getAllProducts,
  useStores,
} from './hooks/useDealsData';
import { useDealsFilters, useInitialCountry } from './hooks/useDealsFilters';

// Components
import {
  DealsHero,
  CategoryTabs,
  FiltersBar,
  DealsGrid,
  SlotMachine3DWrapper,
  LoginPromptModal,
  PurchaseRumiosModal,
  UnlockConfirmModal,
  SpinResultModal,
  AdminLootboxTestModal,
  AdminReelSymbolsModal,
} from './components';

// Types
import type { DealProduct, MysteryType, SpinResult } from './types';
import { calculateStats } from './utils/helpers';

type ModalState =
  | { type: 'none' }
  | { type: 'login' }
  | { type: 'purchase'; highlightType?: MysteryType }
  | { type: 'unlock'; product: DealProduct; mysteryType: MysteryType }
  | { type: 'spin' }
  | { type: 'spin-result'; result: SpinResult }
  | { type: 'admin-test' }
  | { type: 'admin-symbols' };

export default function DealsPage() {
  const { t } = useLanguage();
  const { user, isAdmin } = useAuthStore();

  // Filter state (URL-persisted)
  const { filters, debouncedSearch, setFilter, setFilters } = useDealsFilters();

  // Modal state
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // Data fetching
  const { data: countriesData } = useDealsCountries();
  const { data: detectedCountry } = useDetectCountry();
  const { data: stores = [] } = useStores();

  const countries = countriesData?.countries?.map(c => c.code) || [];

  // Initialize country from detection or storage
  useInitialCountry(
    detectedCountry?.country,
    countries,
    (key, value) => setFilter(key, value),
    filters.country
  );

  // Summary data for stats
  const { data: summary } = useDealsSummary(filters.country, filters.storeId || undefined);

  // Products query
  const productsQuery = useDealsProducts(
    {
      category: filters.category,
      country: filters.country,
      storeId: filters.storeId || undefined,
      search: debouncedSearch || undefined,
      sort: filters.sort,
    },
    Boolean(filters.country)
  );

  const products = getAllProducts(productsQuery.data);
  const stats = calculateStats(products, summary);

  // Modal handlers
  const handleLoginRequired = useCallback(() => {
    setModal({ type: 'login' });
  }, []);

  const handlePurchaseOpen = useCallback((highlightType?: MysteryType) => {
    if (!user) {
      setModal({ type: 'login' });
      return;
    }
    setModal({ type: 'purchase', highlightType });
  }, [user]);

  const handleUnlockOpen = useCallback(
    (product: DealProduct, mysteryType: MysteryType) => {
      if (!user) {
        setModal({ type: 'login' });
        return;
      }
      setModal({ type: 'unlock', product, mysteryType });
    },
    [user]
  );

  const handleSpinOpen = useCallback(() => {
    if (!user) {
      setModal({ type: 'login' });
      return;
    }
    setModal({ type: 'spin' });
  }, [user]);

  const handleUnlockSuccess = useCallback(() => {
    setModal({ type: 'none' });
    // Refetch products to show unlocked item
    productsQuery.refetch();
  }, [productsQuery]);

  const closeModal = useCallback(() => {
    setModal({ type: 'none' });
  }, []);

  const handleAdminTestOpen = useCallback(() => {
    setModal({ type: 'admin-test' });
  }, []);

  const handleAdminSymbolsOpen = useCallback(() => {
    setModal({ type: 'admin-symbols' });
  }, []);

  // Page title
  useEffect(() => {
    document.title = t('deals.pageTitle', 'Deals - Rumio');
  }, [t]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Buttons - Fixed position, visible only to admins */}
      {isAdmin && (
        <div className="fixed bottom-20 right-4 z-40 flex flex-col gap-2">
          <button
            onClick={handleAdminSymbolsOpen}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-green-600 text-white font-medium text-sm shadow-lg hover:bg-green-700 transition-all hover:scale-105"
            title={t('admin.reelSymbols.buttonTitle', 'View Reel Symbols')}
          >
            <Eye className="w-4 h-4" />
            <span>{t('admin.reelSymbols.button', 'View Symbols')}</span>
          </button>
          <button
            onClick={handleAdminTestOpen}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-purple-600 text-white font-medium text-sm shadow-lg hover:bg-purple-700 transition-all hover:scale-105"
            title={t('admin.lootboxTest.buttonTitle', 'Test Lootbox Animations')}
          >
            <TestTube2 className="w-4 h-4" />
            <span>{t('admin.lootboxTest.button', 'Test Lootbox')}</span>
          </button>
        </div>
      )}

      {/* Hero Section */}
      <DealsHero
        stats={stats}
        searchValue={filters.search}
        onSearchChange={(value) => setFilter('search', value)}
        onSpinClick={handleSpinOpen}
        onPurchaseClick={() => handlePurchaseOpen()}
        onLoginRequired={handleLoginRequired}
      />

      {/* Category Tabs */}
      <CategoryTabs
        activeCategory={filters.category}
        onChange={(category) => setFilter('category', category)}
        summary={summary}
        sticky
      />

      {/* Filters Bar */}
      <FiltersBar
        countries={countries}
        stores={stores}
        selectedCountry={filters.country}
        selectedStoreId={filters.storeId}
        selectedSort={filters.sort}
        onCountryChange={(country) => setFilters({ country, storeId: '' })}
        onStoreChange={(storeId) => setFilter('storeId', storeId || '')}
        onSortChange={(sort) => setFilter('sort', sort)}
        isLoading={productsQuery.isLoading}
      />

      {/* Products Grid */}
      <DealsGrid
        products={products}
        category={filters.category}
        isLoading={productsQuery.isLoading}
        isFetchingNextPage={productsQuery.isFetchingNextPage}
        hasNextPage={productsQuery.hasNextPage ?? false}
        onLoadMore={() => productsQuery.fetchNextPage()}
        onMysteryUnlock={handleUnlockOpen}
        onMysteryPurchase={handlePurchaseOpen}
        onLoginRequired={handleLoginRequired}
        searchQuery={debouncedSearch}
      />

      {/* Modals */}
      {modal.type === 'login' && <LoginPromptModal onClose={closeModal} />}

      {modal.type === 'purchase' && (
        <PurchaseRumiosModal onClose={closeModal} highlightType={modal.highlightType} />
      )}

      {modal.type === 'unlock' && (
        <UnlockConfirmModal
          product={modal.product}
          mysteryType={modal.mysteryType}
          onClose={closeModal}
          onSuccess={handleUnlockSuccess}
          onNeedMore={() => setModal({ type: 'purchase', highlightType: modal.mysteryType })}
        />
      )}

      {modal.type === 'spin' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
            onKeyDown={(e) => e.key === 'Escape' && closeModal()}
            role="button"
            tabIndex={-1}
            aria-label={t('modal.close', 'Close modal')}
          />
          <div className="relative max-w-2xl w-full my-8">
            <SlotMachine3DWrapper onLoginRequired={handleLoginRequired} />
            <button
              onClick={closeModal}
              className="mt-4 w-full py-2 text-white/70 hover:text-white text-sm transition-colors"
            >
              {t('modal.close', 'Close')}
            </button>
          </div>
        </div>
      )}

      {modal.type === 'spin-result' && (
        <SpinResultModal result={modal.result} onClose={closeModal} />
      )}

      {modal.type === 'admin-test' && <AdminLootboxTestModal onClose={closeModal} />}

      {modal.type === 'admin-symbols' && <AdminReelSymbolsModal onClose={closeModal} />}
    </div>
  );
}
